/**
 * Startup check for the env keys the API client cannot work without.
 *
 * An empty `REACT_APP_PUBLIC_PATH` is the nasty one: Axios treats it as "no base", every request
 * becomes relative to the dev server, and each screen fails with a 404 that looks like a missing
 * endpoint rather than a missing `.env` value.
 */
export const REQUIRED_ENV_KEYS = [
  'REACT_APP_PUBLIC_PATH',
  'REACT_APP_API_KEY',
  'REACT_APP_RUN_MODE',
] as const;

type EnvSource = Partial<Record<string, string | undefined>>;

const isAbsoluteHttpUrl = (value: string): boolean => /^https?:\/\/\S+$/i.test(value);

/** Returns one human-readable problem per misconfigured key; empty when the env is usable. */
export const findEnvProblems = (env: EnvSource): string[] => {
  const problems: string[] = [];

  for (const key of REQUIRED_ENV_KEYS) {
    if (!env[key]?.trim()) problems.push(`${key} is missing or empty`);
  }

  const base = env.REACT_APP_PUBLIC_PATH?.trim();
  if (base && !isAbsoluteHttpUrl(base)) {
    problems.push(`REACT_APP_PUBLIC_PATH must be an absolute http(s) URL, got "${base}"`);
  }

  return problems;
};

/** Throws one error naming every problem, so a bad `.env` is fixed in a single pass. */
export const assertRequiredEnv = (env: EnvSource): void => {
  const problems = findEnvProblems(env);
  if (problems.length === 0) return;

  throw new Error(
    `Invalid environment configuration:\n- ${problems.join('\n- ')}\n` +
      'Copy the values from the legacy .env or the team secret store into .env, then restart ' +
      '`pnpm dev` (Vite only reads env files at startup).'
  );
};
